import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Heart,
  HeartHandshake,
  Leaf,
  Pause,
  Play,
  Quote,
  ArrowUpRight,
  PlayCircle,
  Users,
} from "lucide-react";
import { api, formatDate, mediaUrl } from "@/lib/api";
import { Seo } from "@/components/Seo";
import "./bka-vibrant-energy.css";
import { Loading, EmeraldGem, ApiOffline } from "@/components/public/Ui";
import { CommunityStrip, EventBand, StoryVideos, TestimonialVideoCard } from "@/components/public/MediaSections";

const ICONS = {
  users: Users,
  leaf: Leaf,
  "heart-handshake": HeartHandshake,
  "book-open": BookOpen,
};

const REVEAL = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.62, ease: [0.22, 1, 0.36, 1] } },
};

const DEFAULT_SECTION_ORDER = [
  "hero",
  "quick_links",
  "gallery",
  "feature",
  "stories",
  "community",
  "event",
  "emerald",
  "testimonials",
  "posts",
  "closing",
];

function uniqueGalleryItems(items) {
  const seen = new Set();
  return items.filter((item) => {
    const key = item?.image;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export default function Home() {
  const [page, setPage] = useState(null);
  const [posts, setPosts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [videos, setVideos] = useState([]);
  const [testimonialVideos, setTestimonialVideos] = useState([]);
  const [testimonialSlide, setTestimonialSlide] = useState(0);
  const [gallerySlide, setGallerySlide] = useState(0);
  const [galleryPaused, setGalleryPaused] = useState(false);
  const [galleryHover, setGalleryHover] = useState(false);

  useEffect(() => {
    // Cache-busted so a CMS save is visible on the next load, and refreshed when the
    // administrator switches back from the CMS tab — never needs a rebuild.
    const load = () => {
      const bust = { params: { _: Date.now() } };
      api.get("/public/pages/home", bust).then((r) => setPage(r.data)).catch(() => setPage(false));
      api.get("/public/posts", { params: { limit: 8, _: Date.now() } }).then((r) => setPosts(r.data)).catch(() => {});
      api.get("/public/programs", bust).then((r) => setPrograms(r.data)).catch(() => {});
      api.get("/public/testimonials", { params: { limit: 9, _: Date.now() } }).then((r) => setTestimonials(r.data)).catch(() => {});
      api.get("/public/videos", { params: { placement: "home", _: Date.now() } }).then((r) => setVideos(r.data || [])).catch(() => {});
      api.get("/public/videos", { params: { placement: "testimonials", _: Date.now() } }).then((r) => setTestimonialVideos(r.data || [])).catch(() => {});
    };
    load();
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, []);

  const gallery = useMemo(() => {
    if (!page) return [];
    const b = page?.body_json || {};
    const curated = Array.isArray(b.gallery_section?.items) ? b.gallery_section.items : [];
    if (curated.length) {
      return uniqueGalleryItems(
        curated
          .filter((item) => item && item.enabled !== false && (item.image_url || item.image))
          .map((item) => ({
            image: mediaUrl(item.image_url || item.image),
            title: item.title || "",
            label: item.label || "",
            href: item.href || "/our-efforts",
          }))
      ).slice(0, 12);
    }
    return uniqueGalleryItems([
      {
        image: mediaUrl(page?.hero_image_url),
        title: b.hero?.heading_line_2 || "Stronger Bahamas",
        label: "Bahamas Kidney Association",
        href: "/about",
      },
      {
        image: mediaUrl(b.feature?.image_url),
        title: b.feature?.title || "Medication Assistance",
        label: "Support in Action",
        href: b.feature?.cta?.href || "/our-efforts",
      },
      ...programs.map((p) => ({
        image: mediaUrl(p.image_url),
        title: p.title,
        label: "Our Efforts",
        href: `/programs/${p.slug}`,
      })),
      ...posts.map((p) => ({
        image: mediaUrl(p.image_url),
        title: p.title,
        label: p.category || "Community",
        href: `/blog/${p.slug}`,
      })),
    ]).slice(0, 10);
  }, [page, posts, programs]);

  useEffect(() => {
    if (galleryPaused || galleryHover || gallery.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setGallerySlide((current) => (current + 1) % gallery.length);
    }, 4300);
    return () => window.clearInterval(timer);
  }, [galleryPaused, galleryHover, gallery.length]);

  if (page === null) return <Loading label="Loading the homepage" />;
  if (page === false) return <ApiOffline what="the homepage" />;

  const b = page?.body_json || {};
  const hero = b.hero || {};
  const gallerySection = b.gallery_section || {};
  const testimonialsSection = b.testimonials_section || {};
  const postsSection = b.posts_section || {};
  const closing = b.closing_band || {};
  const meta = b.sections_meta || {};
  const storyItems = videos.length
    ? videos.map((v) => ({
        id: v.id,
        youtube_id: v.youtube_id || null,
        video_url: v.video_url || "",
        poster_url: v.thumbnail_url || "",
        thumbnail_url: v.thumbnail_url || "",
        title: v.title,
        name: v.title,
        caption: v.caption || "",
        role: v.caption || "",
        description: v.description || "",
        cta_label: v.cta_label || "",
        cta_href: v.cta_href || "",
      }))
    : b.stories?.items || [];
  const isOn = (key) => meta[key]?.enabled !== false;
  const orderOf = (key) =>
    Number(meta[key]?.order ?? (DEFAULT_SECTION_ORDER.indexOf(key) + 1) * 10);
  // Video testimonials always get reserved slots so CMS-published videos are never
  // pushed out by quotes, and the row always fills the panel width.
  const testimonialVideoCards = testimonialVideos.map((v) => ({
    kind: "video",
    id: v.id,
    youtube_id: v.youtube_id || null,
    video_url: v.video_url || "",
    poster_url: v.thumbnail_url || "",
    thumbnail_url: v.thumbnail_url || "",
    title: v.title,
    name: v.title,
    caption: v.caption || "",
    role: v.caption || "",
    description: v.description || "",
  }));
  const reservedVideos = testimonialVideoCards.slice(0, 2);
  const quotesPerPage = Math.max(1, 3 - reservedVideos.length);
  const testimonialPages = Math.max(1, Math.ceil(testimonials.length / quotesPerPage));
  const visibleTestimonials = testimonials.slice(
    testimonialSlide * quotesPerPage,
    testimonialSlide * quotesPerPage + quotesPerPage
  );
  const testimonialCards = [
    ...visibleTestimonials.map((t) => ({ ...t, kind: "quote" })),
    ...reservedVideos,
  ];
  const activeGallery = gallery[gallerySlide] || null;
  const nextGallery = gallery.length ? gallery[(gallerySlide + 1) % gallery.length] : null;
  const thirdGallery = gallery.length ? gallery[(gallerySlide + 2) % gallery.length] : null;

  const blocks = [
    { key: "hero", node: (
      <section data-testid="home-hero" className="bka-hero2">
        <div className="bka-hero2__photo" aria-hidden="true">
          {page?.hero_image_url && (
            <img src={mediaUrl(page.hero_image_url)} alt={hero.image_alt || ""} loading="eager" />
          )}
        </div>

        <svg className="bka-hero2__curve" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0,0 L55,0 C41,26 39,70 61,100 L0,100 Z" className="bka-hero2__curve-green" />
          <path d="M60,0 C46,26 44,70 66,100" className="bka-hero2__curve-orange" />
          <path d="M100,62 C92,78 86,90 74,100 L100,100 Z" className="bka-hero2__curve-orange-fill" />
        </svg>

        <div className="bka-container relative z-10 grid items-center gap-8 py-16 lg:grid-cols-[1.05fr_0.95fr] lg:py-24">
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.08 } } }}
            className="max-w-[640px]"
          >
            <motion.p variants={REVEAL} className="bka-text-lime text-xs font-extrabold uppercase tracking-[0.3em] sm:text-sm">
              {hero.overline || "Bahamas Kidney Association"}
            </motion.p>

            <motion.h1
              variants={REVEAL}
              className="mt-5 font-display text-[clamp(2.9rem,6vw,5.6rem)] font-black leading-[0.92] tracking-[-0.045em] text-white"
            >
              {hero.heading_line_1 || "A Healthier People."}
              <span className="mt-1 block bka-text-orange">{hero.heading_line_2 || "A Stronger Bahamas."}</span>
            </motion.h1>

            <motion.p variants={REVEAL} className="mt-6 max-w-[30rem] text-base font-medium leading-[1.7] text-white/90 sm:text-lg">
              {hero.subheading}
            </motion.p>

            <motion.div variants={REVEAL} className="mt-9 flex flex-wrap gap-4">
              <Link
                to={hero.primary_cta?.href || "/donate"}
                data-testid="home-hero-donate"
                className="bka-action bka-action--orange bka-action--xl"
              >
                <Heart className="h-5 w-5" />
                {hero.primary_cta?.label || "Donate Now"}
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                to={hero.secondary_cta?.href || "/get-support"}
                data-testid="home-hero-support"
                className="bka-action bka-action--outline-light bka-action--xl"
              >
                {hero.secondary_cta?.label || "Get Support"}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </motion.div>
          </motion.div>

          <motion.ul
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.12, delayChildren: 0.3 } } }}
            className="bka-hero2__pillars"
            data-testid="home-hero-pillars"
          >
            {(hero.pillars || ["Education", "Prevention", "Support", "Community"]).map((word) => (
              <motion.li key={word} variants={REVEAL}>{word}</motion.li>
            ))}
          </motion.ul>
        </div>
      </section>
    ) },

    { key: "quick_links", node: (
      <section className="relative z-20 bg-white py-8 lg:py-10" data-testid="home-quick-links">
        <div className="bka-container">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {(b.quick_links || []).filter((card) => card && card.enabled !== false).map((card, i) => {
              const Icon = ICONS[card.icon] || Users;
              const photo = card.image_url ? mediaUrl(card.image_url) : gallery[(i + 1) % Math.max(gallery.length, 1)]?.image;
              return (
                <Link
                  key={card.href}
                  to={card.href}
                  data-testid={`home-quick-link-${i}`}
                  className={`bka-link-card bka-link-card--${i % 2 === 0 ? "green" : "orange"}`}
                  aria-label={`${card.title} — ${card.cta}`}
                >
                  {photo && <img src={photo} alt="" aria-hidden="true" className="bka-link-card__photo" />}
                  <span className="bka-link-card__body">
                    <Icon className="h-7 w-7" strokeWidth={2.4} aria-hidden="true" />
                    <span className="mt-3 block font-display text-xl font-black uppercase leading-none tracking-[0.01em] sm:text-2xl">
                      {card.title}
                    </span>
                    <span className="mt-2 block max-w-[11rem] text-[13px] font-semibold leading-snug opacity-90">
                      {card.text}
                    </span>
                  </span>
                  <span className="bka-link-card__arrow" aria-hidden="true">
                    <ArrowRight className="h-5 w-5" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    ) },

    { key: "gallery", node: gallery.length > 0 && (
        <section className="bka-gallery-section" data-testid="home-gallery">
          <div className="bka-container relative z-10 py-16 lg:py-24">
            <div className="mb-10 flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
              <div className="max-w-2xl">
                <p className="bka-overline bka-text-orange">{gallerySection.overline || "BKA In Action"}</p>
                <h2 className="mt-4 font-display text-4xl font-black leading-[0.98] tracking-[-0.035em] text-white sm:text-5xl lg:text-6xl">
                  {gallerySection.heading || "Real people. Real community."}{" "}
                  <span className="bka-text-orange">{gallerySection.heading_accent || "Visible impact."}</span>
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setGalleryPaused((v) => !v)}
                  className="bka-gallery-control"
                  data-testid="home-gallery-toggle"
                  aria-label={galleryPaused ? "Play gallery" : "Pause gallery"}
                >
                  {galleryPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => setGallerySlide((s) => (s - 1 + gallery.length) % gallery.length)}
                  className="bka-gallery-control"
                  data-testid="home-gallery-prev"
                  aria-label="Previous gallery image"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => setGallerySlide((s) => (s + 1) % gallery.length)}
                  className="bka-gallery-control"
                  data-testid="home-gallery-next"
                  aria-label="Next gallery image"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div
              className="grid gap-4 lg:grid-cols-[1.55fr_0.72fr]"
              onMouseEnter={() => setGalleryHover(true)}
              onMouseLeave={() => setGalleryHover(false)}
            >
              <div className="relative min-h-[430px] overflow-hidden rounded-[32px] bg-bka-navyDeep sm:min-h-[540px]">
                <AnimatePresence mode="wait">
                  {activeGallery && (
                    <motion.div
                      key={`${activeGallery.image}-${gallerySlide}`}
                      initial={{ opacity: 0, scale: 1.035 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute inset-0"
                    >
                      <img src={activeGallery.image} alt={activeGallery.title || "BKA community activity"} className="h-full w-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-bka-navyDeep/95 via-bka-navyDeep/20 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-7 sm:p-10 lg:p-12">
                        <p className="bka-overline bka-text-orange">{activeGallery.label}</p>
                        <h3 className="mt-3 max-w-3xl font-display text-3xl font-black leading-tight text-white sm:text-4xl">
                          {activeGallery.title}
                        </h3>
                        <Link to={activeGallery.href || "/our-efforts"} className="mt-6 inline-flex items-center gap-2 text-sm font-extrabold text-white">
                          {gallerySection.item_cta || "Explore the story"} <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="flex flex-col gap-4">
                <div className="grid flex-1 gap-4 sm:grid-cols-2 lg:grid-cols-1 lg:grid-rows-2">
                {[nextGallery, thirdGallery].filter(Boolean).map((item, idx) => (
                  <button
                    key={`${item.image}-${idx}`}
                    type="button"
                    onClick={() => setGallerySlide((gallerySlide + idx + 1) % gallery.length)}
                    className="group relative min-h-[210px] overflow-hidden rounded-[28px] text-left lg:min-h-[180px]"
                  >
                    <img src={item.image} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-bka-navyDeep/92 via-bka-navyDeep/10 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] bka-text-orange">{item.label}</p>
                      <p className="mt-2 line-clamp-2 font-display text-xl font-black leading-tight text-white">{item.title}</p>
                    </div>
                  </button>
                ))}
                </div>

                {gallerySection.cta_href !== "" && (
                  (() => {
                    const href = gallerySection.cta_href || "/gallery";
                    const cls =
                      "group inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border-2 border-bka-orangeLight/70 px-7 text-sm font-extrabold uppercase tracking-[0.12em] text-white transition-all duration-300 hover:border-bka-orangeLight hover:bg-bka-orangeLight/15";
                    const label = (
                      <>
                        {gallerySection.cta_label || "See more Galleries"}
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                      </>
                    );
                    return /^https?:\/\//i.test(href) ? (
                      <a href={href} target="_blank" rel="noopener noreferrer" data-testid="home-gallery-see-more" className={cls}>
                        {label}
                      </a>
                    ) : (
                      <Link to={href} data-testid="home-gallery-see-more" className={cls}>
                        {label}
                      </Link>
                    );
                  })()
                )}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {gallery.map((item, i) => (
                <button
                  key={`${item.image}-dot`}
                  type="button"
                  onClick={() => setGallerySlide(i)}
                  aria-label={`Show gallery image ${i + 1}`}
                  aria-current={i === gallerySlide}
                  data-testid={`home-gallery-dot-${i}`}
                  className={`h-3 rounded-full transition-all duration-300 ${i === gallerySlide ? "w-10 bka-bg-orange" : "w-5 bg-white/30 hover:bg-white/60"}`}
                />
              ))}
            </div>
          </div>
        </section>
      ) },

    { key: "feature", node: b.feature && (
        <section className="overflow-hidden bka-bg-orange">
          <div className="grid lg:grid-cols-2">
            <div className="relative min-h-[430px] overflow-hidden lg:min-h-[620px]">
              <img src={mediaUrl(b.feature.image_url)} alt={b.feature.title} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bka-orange-image-fade" />
              <div className="bka-photo-pulse" aria-hidden="true" />
            </div>
            <div className="flex items-center px-6 py-14 sm:px-10 lg:px-16 lg:py-20 xl:px-20">
              <div className="max-w-xl">
                <p className="bka-overline text-bka-navyDeep">{b.feature.eyebrow || "Flagship Program"}</p>
                <h2 className="mt-5 font-display text-4xl font-black leading-[0.98] tracking-[-0.035em] text-bka-navyDeep sm:text-5xl lg:text-6xl">
                  {b.feature.title}
                </h2>
                <p className="mt-6 text-base font-semibold leading-[1.8] text-bka-navyDeep/80">{b.feature.text}</p>
                {(b.feature.medications || []).length > 0 && (
                  <ul className="mt-6 flex flex-wrap gap-2" data-testid="home-medication-list">
                    {b.feature.medications.map((m) => (
                      <li
                        key={m}
                        className="rounded-full bg-white/85 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.08em] text-bka-navyDeep"
                      >
                        {m}
                      </li>
                    ))}
                  </ul>
                )}
                <Link to={b.feature.cta?.href || "/our-efforts"} data-testid="home-medication-cta" className="bka-action bka-action--green mt-8">
                  {b.feature.cta?.label || "Learn More"}<ArrowRight className="h-4 w-4" />
                </Link>
                {b.feature.note && <p className="mt-5 text-xs font-semibold leading-relaxed text-bka-navyDeep/60">{b.feature.note}</p>}
              </div>
            </div>
          </div>
        </section>
      ) },

    { key: "stories", node: (
      <StoryVideos
        overline={b.stories?.overline}
        heading={b.stories?.heading}
        intro={b.stories?.intro}
        items={storyItems}
      />
    ) },

    { key: "community", node: (
      <CommunityStrip
        overline={b.community?.overline}
        heading={b.community?.heading}
        intro={b.community?.intro}
        items={(b.community?.items || []).filter((item) => item && item.enabled !== false)}
      />
    ) },

    { key: "event", node: <EventBand event={b.event} /> },

    { key: "emerald", node: b.emerald_panel && (
      <section className="bg-[#f7fbf8] pt-16 pb-7 lg:pt-24">
        <div className="bka-container flex flex-col gap-7">
          <div data-testid="home-emerald-panel" className="emerald-feature-card min-h-[440px]">
              <div className="bka-grain absolute inset-0" aria-hidden="true" />
              <div className="emerald-feature-card__content">
                <p className="bka-overline bka-text-orange">{b.emerald_panel.eyebrow}</p>
                <h2 className="mt-4 font-display text-3xl font-black leading-[1.02] text-white sm:text-4xl lg:text-5xl">{b.emerald_panel.title}</h2>
                <p className="mt-5 max-w-xl text-sm font-medium leading-[1.8] text-white/75">{b.emerald_panel.text}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to={b.emerald_panel.primary_cta?.href || "/emerald-honours#nominate"} data-testid="home-emerald-nominate" className="bka-action bka-action--orange">
                    {b.emerald_panel.primary_cta?.label || "Nominate Now"}<ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to={b.emerald_panel.secondary_cta?.href || "/emerald-honours"} data-testid="home-emerald-videos" className="bka-action bka-action--glass">
                    <PlayCircle className="h-4 w-4" />{b.emerald_panel.secondary_cta?.label || "Watch the Highlights"}
                  </Link>
                </div>
              </div>
              <div className="emerald-feature-card__visual">
                <img
                  src={mediaUrl(b.emerald_panel.image_url || "/api/media/6ad98fbe4a3959202042dfcd/file")}
                  alt="Emerald Honours crystal awards"
                  loading="lazy"
                  className="h-auto w-full rounded-[22px] object-cover shadow-[0_30px_70px_-40px_rgba(0,0,0,0.8)]"
                  data-testid="home-emerald-visual"
                />
              </div>
            </div>
        </div>
      </section>
    ) },

    { key: "testimonials", node: (
      <section className="bg-[#f7fbf8] pb-16 pt-7 lg:pb-24">
        <div className="bka-container">
          <div className="relative overflow-hidden rounded-[30px] border-t-[6px] border-bka-orange bg-white p-7 shadow-[0_30px_80px_-48px_rgba(0,72,45,0.45)] sm:p-10" data-testid="home-testimonials">
            <div
              className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full"
              aria-hidden="true"
              style={{ background: "radial-gradient(circle, rgba(200,90,22,.12), transparent 70%)" }}
            />
            <div className="relative flex items-center justify-between gap-4">
              <div>
                <p className="bka-overline bka-text-orange">{testimonialsSection.overline || "Community Voices"}</p>
                <h2 className="mt-2 font-display text-3xl font-black text-bka-navy sm:text-4xl lg:text-5xl">{testimonialsSection.heading || b.testimonials_heading || "What People Are Saying"}</h2>
                <div className="bka-accent-rule mt-5" />
              </div>
              {testimonialPages > 1 && (
                <div className="flex gap-2">
                  <button type="button" aria-label="Previous testimonials" onClick={() => setTestimonialSlide((s) => (s - 1 + testimonialPages) % testimonialPages)} className="bka-round-control"><ChevronLeft className="h-4 w-4" /></button>
                  <button type="button" aria-label="Next testimonials" onClick={() => setTestimonialSlide((s) => (s + 1) % testimonialPages)} className="bka-round-control"><ChevronRight className="h-4 w-4" /></button>
                </div>
              )}
            </div>
            <div
              className={`mt-7 grid gap-4 ${
                testimonialCards.length >= 3 ? "md:grid-cols-3" : testimonialCards.length === 2 ? "md:grid-cols-2" : "md:grid-cols-1"
              }`}
              data-testid="home-testimonials-grid"
            >
              {testimonialCards.length ? testimonialCards.map((card) => (
                card.kind === "video" ? (
                  <div key={`v-${card.id}`} data-testid={`home-testimonial-video-${card.id}-wrap`}>
                    <TestimonialVideoCard item={card} testId={`home-testimonial-video-${card.id}`} />
                  </div>
                ) : (
                  <figure key={card.id} className="rounded-2xl border-l-4 bka-border-orange bg-bka-cream p-6 transition-transform duration-300 hover:-translate-y-1">
                    <Quote className="mb-3 h-7 w-7 text-bka-orange/55" aria-hidden="true" />
                    <blockquote className="text-sm font-semibold leading-[1.8] text-bka-ink/85">“{card.testimonial}”</blockquote>
                    <figcaption className="mt-4 text-xs font-extrabold uppercase tracking-[0.1em] text-bka-emerald">{card.full_name}</figcaption>
                  </figure>
                )
              )) : (
                <div className="rounded-2xl bg-bka-cream p-6 text-sm font-semibold leading-relaxed text-bka-ink/70">
                  {testimonialsSection.empty_text ||
                    "BKA community stories and testimonials will appear here as approved content is published."}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    ) },

    { key: "posts", node: posts.length > 0 && (
        <section className="bg-white py-16 lg:py-24" data-testid="home-latest-posts">
          <div className="bka-container">
            <div className="mb-10 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
              <div>
                <p className="bka-overline bka-text-orange">{postsSection.overline || "Stories + Updates"}</p>
                <h2 className="mt-3 font-display text-4xl font-black tracking-[-0.035em] text-bka-navy sm:text-5xl">{postsSection.heading || b.posts_heading || "Latest Posts"}</h2>
              </div>
              <Link to={postsSection.cta_href || "/blog"} className="inline-flex items-center gap-2 text-sm font-extrabold text-bka-emerald">{postsSection.cta_label || "View all stories"} <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="grid gap-5 md:grid-cols-3">
              {posts.slice(0, 3).map((p, i) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className={`bka-story-card group ${i === 0 ? "md:col-span-2 md:row-span-2" : ""}`}>
                  <img src={mediaUrl(p.image_url)} alt={p.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-bka-navyDeep/95 via-bka-navyDeep/15 to-transparent" />
                  <div className="relative mt-auto p-6 sm:p-7">
                    <p className="bka-overline bka-text-orange">{p.category}</p>
                    <h3 className={`mt-3 font-display font-black leading-tight text-white ${i === 0 ? "text-3xl sm:text-4xl" : "text-xl"}`}>{p.title}</h3>
                    <p className="mt-3 text-xs font-semibold text-white/65">{formatDate(p.published_at)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) },

    { key: "closing", node: (
      <section className="bka-closing-band">
        <div className="bka-closing-band__ring" aria-hidden="true" />
        <div className="bka-container relative z-10 flex flex-col justify-between gap-8 py-14 lg:flex-row lg:items-center lg:py-20">
          <div className="max-w-3xl">
            <p className="bka-overline text-bka-navyDeep">{closing.overline || "One Association. A Healthier Bahamas."}</p>
            <h2 className="mt-4 font-display text-4xl font-black leading-[0.98] tracking-[-0.04em] text-bka-navyDeep sm:text-5xl lg:text-6xl">
              {closing.heading || "Compassion. Community."}{" "}
              <span className="underline decoration-white/70 decoration-[6px] underline-offset-[10px]">{closing.heading_accent || "Action."}</span>
            </h2>
          </div>
          <div className="flex shrink-0 flex-wrap gap-3">
            <Link to={closing.primary_cta?.href || "/donate"} data-testid="home-closing-primary" className="bka-action bka-action--green">{closing.primary_cta?.label || "Donate Now"} <ArrowRight className="h-4 w-4" /></Link>
            <Link to={closing.secondary_cta?.href || "/get-involved"} data-testid="home-closing-secondary" className="bka-action bka-action--outline-dark">{closing.secondary_cta?.label || "Get Involved"}</Link>
          </div>
        </div>
      </section>
    ) },
  ];

  const ordered = blocks
    .filter((block) => block.node && isOn(block.key))
    .sort((x, y) => orderOf(x.key) - orderOf(y.key));

  return (
    <>
      <Seo
        title={page?.seo_title || "Bahamas Kidney Association"}
        description={page?.seo_description}
        canonicalPath="/"
      />
      {ordered.map((block) => (
        <Fragment key={block.key}>{block.node}</Fragment>
      ))}
    </>
  );
}
